// src/lib/dateUtils.js
import dayjs from "lib/dayjs";
import Holidays from "date-holidays";

// ──────────────────────────────────────────────
// Initialize NY-specific U.S. market calendar
// ──────────────────────────────────────────────
const hd = new Holidays("US", "ny"); // Federal + NY state bank holidays

/** True if date is a weekend or market holiday. */
export function isHolidayOrWeekend(date) {
  const d = dayjs(date);
  const dow = d.day();
  if (dow === 0 || dow === 6) return true; // Sat/Sun
  return Boolean(hd.isHoliday(d.toDate())); // Fed/NY holiday
}

/** True if date is a valid NY market business day. */
export function isBusinessDay(date) {
  return !isHolidayOrWeekend(date);
}

/** Add N business days (skipping weekends & holidays). */
export function addBusinessDays(date, days) {
  let d = dayjs(date);
  let added = 0;
  while (added < days) {
    d = d.add(1, "day");
    if (isBusinessDay(d)) added++;
  }
  return d;
}

/** Subtract N business days (skipping weekends & holidays). */
export function subtractBusinessDays(date, days) {
  let d = dayjs(date);
  let subtracted = 0;
  while (subtracted < days) {
    d = d.subtract(1, "day");
    if (isBusinessDay(d)) subtracted++;
  }
  return d;
}

/**
 * Adjust a date to the nearest valid business day under a convention.
 * @param {"following"|"preceding"|"modifiedFollowing"|"modifiedPreceding"} convention
 */
export function adjustToBusinessDay(date, convention = "following") {
  let d = dayjs(date);
  if (isBusinessDay(d)) return d;

  switch (convention) {
    case "following": {
      while (!isBusinessDay(d)) d = d.add(1, "day");
      break;
    }
    case "preceding": {
      while (!isBusinessDay(d)) d = d.subtract(1, "day");
      break;
    }
    case "modifiedFollowing": {
      const startMonth = d.month();
      while (!isBusinessDay(d)) d = d.add(1, "day");
      if (d.month() !== startMonth) {
        d = dayjs(date);
        while (!isBusinessDay(d)) d = d.subtract(1, "day");
      }
      break;
    }
    case "modifiedPreceding": {
      const startMonth = d.month();
      while (!isBusinessDay(d)) d = d.subtract(1, "day");
      if (d.month() !== startMonth) {
        d = dayjs(date);
        while (!isBusinessDay(d)) d = d.add(1, "day");
      }
      break;
    }
    default:
      throw new Error(`Invalid convention: ${convention}`);
  }

  return d;
}

/** Next valid market business day. */
export function nextBusinessDay(date) {
  let d = dayjs(date).add(1, "day");
  while (!isBusinessDay(d)) d = d.add(1, "day");
  return d;
}

/** Previous valid market business day. */
export function prevBusinessDay(date) {
  let d = dayjs(date).subtract(1, "day");
  while (!isBusinessDay(d)) d = d.subtract(1, "day");
  return d;
}

/** Return all valid NY business days between two dates (inclusive). */
export function getBusinessDays(start, end) {
  const s = dayjs(start);
  const e = dayjs(end);
  const list = [];
  for (let d = s; d.isSameOrBefore(e, "day"); d = d.add(1, "day")) {
    if (isBusinessDay(d)) list.push(d.format("YYYY-MM-DD"));
  }
  return list;
}

/** Format a date as MM/DD/YYYY for UI display. */
export function formatDateMmDdYyyy(date) {
  const d = dayjs(date);
  return d.isValid() ? d.format("MM/DD/YYYY") : "";
}